// The selected unit, at the top of the right rail: its symbol, what it is, how it is holding up, and
// every order still in flight to it with the countdown to the next transition (spec §5).

import { useMemo } from 'react';
import type { Order, Snapshot, Unit } from '../../sim/types';
import { TICK_SEC } from '../../sim/types';
import { UNIT_DEFS } from '../../sim/scenario';
import { card } from '../../sim/deck';
import { symbolUrl } from '../symbol';
import { Bar, Chip, fmtDur } from './ui';

const POSTURE_LABEL: Record<Unit['posture'], string> = {
  move: 'moving', attack: 'attacking', defend: 'defending', hold: 'holding', withdraw: 'withdrawing',
};

const STATUS_LABEL: Record<Order['status'], string> = {
  queued: 'in the net queue',
  transmitting: 'transmitting',
  received: 'acknowledged',
  executing: 'executing',
  done: 'complete',
  cancelled: 'cancelled',
  rejected: 'refused',
};

/** Ticks until the order's next status change, or null when it has none left. */
function ticksToNext(o: Order, t: number): number | null {
  switch (o.status) {
    case 'queued': return Math.max(0, o.netStart - t);
    case 'transmitting': return Math.max(0, o.txDone - t);
    case 'received': return Math.max(0, o.execStart - t);
    default: return null;
  }
}

export function UnitCard({ unit, snapshot }: { unit: Unit; snapshot: Snapshot }) {
  const def = UNIT_DEFS[unit.type];
  const sidc = unit.side === 0 ? def.sidcBlue : def.sidcRed;
  const symbol = useMemo(() => symbolUrl(sidc), [sidc]);
  const pending = snapshot.orders.filter(
    (o) => o.unitId === unit.id && o.status !== 'done' && o.status !== 'cancelled' && o.status !== 'rejected',
  );
  const broken = unit.brokenUntil > snapshot.tick;

  return (
    <section data-testid="unit-card" className="wt-card shrink-0">
      <header className="flex items-center gap-2">
        <img src={symbol} alt="" width={28} height={28} className="shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="mono truncate text-[13px] tracking-[0.1em] text-paper">{unit.callsign}</div>
          <div className="truncate text-[10px] text-fog">{def.label}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Chip tone={unit.inContact ? 'hostile' : 'neutral'} active={unit.inContact}>
            {unit.inContact ? 'in contact' : POSTURE_LABEL[unit.posture]}
          </Chip>
          {(unit.pinned || broken) && (
            <div className="flex gap-1">
              {unit.pinned && <Chip tone="warn" active>pinned</Chip>}
              {broken && <Chip tone="hostile" active>broken</Chip>}
            </div>
          )}
        </div>
      </header>

      <div className="mt-2 space-y-1">
        <Bar label="str" value={unit.strength} color="var(--color-friendly)" title="Strength" />
        <Bar label="sup" value={unit.suppression} color="var(--color-warn)" title="Suppression" />
        <Bar label="mor" value={unit.morale} color="var(--color-sustain)" title="Morale" />
        <Bar label="fue" value={unit.fuel} color="var(--color-isr)" title="Fuel" />
        <Bar label="amm" value={unit.ammo} color="var(--color-command)" title="Ammunition" />
      </div>

      <div className="mt-2 border-t border-slate-700 pt-1.5">
        <div className="mono text-[9px] uppercase tracking-[0.25em] text-fog">Orders in flight</div>
        {pending.length === 0 ? (
          <div className="wt-empty mt-1" data-testid="orders-empty">
            Nothing in flight — {unit.callsign} is carrying out its last order.
          </div>
        ) : (
          <ul className="mt-1 space-y-1">
            {pending.map((o) => {
              const left = ticksToNext(o, snapshot.tick);
              return (
                <li key={o.id} className="mono flex items-baseline justify-between gap-2 text-[10px]">
                  <span className="truncate text-paper">{card(o.card).label}</span>
                  <span className="shrink-0 text-fog">
                    {STATUS_LABEL[o.status]}
                    {left !== null && ` · ${fmtDur(left * TICK_SEC)}`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
